import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from "react-redux";
import { getIdolArray } from "@/shared/store/slices/idolSlice";
import { useDeleteIdol, canDeleteIdol } from '@/hooks/useDeleteIdol';
import { useUnmountSafeTimeout } from '@/hooks/useUnmountSafeTimeout';
import Axios from '@/shared/api/axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import placeholder_portrait_300x300 from "@/assets/imgs/placeholders/portrait_300x300.png";
import placeholder_cover_1600x700 from "@/assets/imgs/placeholders/cover_1600x700.jpg";


import MainNav from '@/components/layout/AppNavbar';
import MessageModal from '@/components/ui/MessageModal';
import StatusModal from '@/components/ui/StatusModal';
import LoadingButton from '@/components/ui/LoadingButton';
import { processImage } from '@/shared/utils/imageProcessor';
import { GENDER_LABEL_TO_VALUE } from '@/shared/constants/idolOptions';

import {
    Row,
    Col,
    Button,
    Container,
    Form,
} from 'react-bootstrap';

// get-idol-info 回傳的性別標籤轉成下拉選單與 /idol/update 用的數字（見 GENDER_LABEL_TO_VALUE）。
// 沒轉換的話已設定性別的偶像進來會顯示「請選擇性別」，而且 handleSubmit 比對 initInfo 時永遠判定有變更
const toGenderValue = (label) => {
    if (label === null) return null;
    if (!Object.prototype.hasOwnProperty.call(GENDER_LABEL_TO_VALUE, label)) {
        console.error('[IdolUpdate] 無法對應的性別：', label);
        return null;
    }
    return GENDER_LABEL_TO_VALUE[label];
};

const IdolUpdate = () => {
    const dispatch = useDispatch();
    const params = useParams();
    const navigate = useNavigate();
    // 成功後延遲導頁用：期間若已離開頁面，卸載時清掉 timer，不會被拉回來
    const setRedirectTimeout = useUnmountSafeTimeout();
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const [isFormChanged, setFormChanged] = useState(false);
    // 偶像資料是否成功載入。抓不到就不能送出：initInfo 會停在 {}，
    // handleSubmit 的差異比對（info[key] !== initInfo[key]）會判定每個欄位都有異動，
    // 把整份資料以空字串覆蓋回後端
    const [isInfoLoaded, setInfoLoaded] = useState(false);
    const [isInfoLoading, setInfoLoading] = useState(true);
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [coverPreview, setCoverPreview] = useState(null);

    const idolId = useSelector((state) => state.user.idolId);
    const idolList = useSelector((state) => state.idol.list);
    const deleteIdolAndSwitch = useDeleteIdol();

    const [initInfo, setInitInfo] = useState({}); 

    const [info, setInfo] = useState({
        "idol_nick_name": "", // 偶像暱稱（偶像帳號）
        "idol_full_name": "", // 偶像全名
        "avatar_path": "", // 偶像頭像
        "cover_path": "", // 偶像封面圖
        "idol_bio": "", // 偶像簡介
        "age": "", // 偶像年紀
        "gender": "", // 偶像性別
        "sexual_orientation": "", // 性取向
        "height": "", // 偶像身高
        "body_type": "", // 偶像體重
        "interests": "", // 偶像興趣
        "education": "", // 偶像教育
        "occupation": "", // 偶像工作
        "relationship_status": "", // 感情狀況
        "link_worlds": "", // 連結世界觀
        "preferences": "", // 受眾目標
        "prompt_word": "", // AI 提示詞
        "is_official": 0, // 是否官方認證
        "is_original": 0, // 是否原創
        "is_nsfw": 0 // 是否工作場合不適
        }
    );

    // 列表中對應的偶像，用來判斷刪除按鈕；資料載入前（info.id 未定）找不到，不顯示刪除
    const editingIdol = idolList.find((idol) => idol.idol_id === info.id);

    const [form] = useState({
        idol_id: idolId,
    })
    const [validated, setValidated] = useState(false);

    // API 非成功狀態碼統一由 StatusModal 顯示（含攔截器附加的 HTTP_xxx）
    const statusModalRef = useRef(null);
    const [currentStatusCode, setCurrentStatusCode] = useState('');

    const modal1000 = useRef(null);
    const modal2021 = useRef(null);
    const modalSize = useRef(null);
    const modalType = useRef(null);
    const modalImageProcessError = useRef(null);
    const modalDelete = useRef(null);
    const modalDeleteSuccess = useRef(null);


    const getIdolInfo = async() => {
        setInfoLoading(true);
        try {
            const res = await Axios.post("/idol/get-idol-info", {
                me_idol_id: idolId,
                idol_uuid: params.idolUuid
            })
            if (res.data.status_code === 'SYSTEM_1000') {
                const idolInfo = { ...res.data.data, gender: toGenderValue(res.data.data.gender) };
                setInfo(idolInfo);
                setInitInfo(idolInfo);
                setInfoLoaded(true);
            } else {
                setCurrentStatusCode(res.data.status_code);
                statusModalRef.current?.show();
            }
        } catch (error) {
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Get idol info error:', error);
            }
        } finally {
            setInfoLoading(false);
        }
    } 

    const handleChange = async e => {
        let value = e.target.value;

        if(e.target.type === 'radio'){
            value = Number(e.target.value);
        } else if(e.target.type === 'file') {
            const file = e.target.files[0];
            // 取出檔案後就清空欄位，與發文、世界觀一致：否則選到壞圖跳提示後，再選同一個檔案不會觸發 change
            e.target.value = '';
            // 檢查檔案大小不超過 10 MB
            if(file.size > 10 * 1024 * 1024) {
                modalSize.current.show();
                return;
            }
            // 檢查是否為 JPG 或 PNG
            if(file.type !== 'image/jpg' && file.type !== 'image/jpeg' && file.type !== 'image/png') {
                modalType.current.show();
                return;
            }

            try {
                if (e.target.name === 'avatar_path') {
                    const results = await processImage(file, {
                        sizes: [
                            { width: 100, prefix: 'small' },
                            { width: 300, prefix: 'big' }
                        ]
                    });
                    
                    setInfo(prevInfo => ({
                        ...prevInfo,
                        ...form,
                        avatar_path: results.small,
                        big_avatar_path: results.big
                    }));
                    
                    // Clean up previous preview URL
                    if (avatarPreview && avatarPreview.startsWith('blob:')) {
                        URL.revokeObjectURL(avatarPreview);
                    }
                    
                    // Create preview URL for display
                    const previewUrl = URL.createObjectURL(results.big);
                    setAvatarPreview(previewUrl);
                }
                
                if (e.target.name === 'cover_path') {
                    const results = await processImage(file, {
                        sizes: [
                            { width: 450, height: 150, prefix: 'small' },
                            { width: 1350, height: 450, prefix: 'big' }
                        ]
                    });

                    setInfo(prevInfo => ({
                        ...prevInfo,
                        ...form,
                        cover_path: results.small,
                        big_cover_path: results.big
                    }));

                    // Clean up previous preview URL
                    if (coverPreview && coverPreview.startsWith('blob:')) {
                        URL.revokeObjectURL(coverPreview);
                    }

                    // Create preview URL for display
                    const previewUrl = URL.createObjectURL(results.big);
                    setCoverPreview(previewUrl);
                }
                
                // Enable save button when file is uploaded
                setFormChanged(true);
            } catch (error) {
                // 圖片讀取／解碼失敗（例如檔案損毀）
                console.error('Error processing image:', error);
                modalImageProcessError.current.show();
            }
            return;
        } else if ((e.target.type === 'number' || e.target.type === 'select-one') && value.trim() !== '' && !isNaN(value)) {
            // 只轉數字欄位（年齡、身高）與下拉選單（性別的選項值是 "0"/"1"/"2"）：handleSubmit 用 !== 比對 initInfo
            // 判斷欄位是否變更，型別要與後端回傳的一致。文字欄位不能轉，否則體重打不出小數點（"50." → 50）、"007" 變 7
            value = Number(value);
        }

        setInfo(prevInfo => ({
            ...prevInfo,
            ...form,
            [e.target.name]: value,
        }));

        setFormChanged(true);
    }

    const handleSubmit = async(e) => {

        const formCheck = e.currentTarget;
        e.preventDefault();

        if (formCheck.checkValidity() === false) {
            e.stopPropagation();
            setValidated(true);
            return
        }

        setIsLoading(true);

        const formData = new FormData();
        Object.keys(info).forEach(key => {
            if (info[key] !== initInfo[key]) {
                formData.append(key, info[key]);
            }
        });

        try {
            const res = await Axios.post("/idol/update", formData);
            switch(res.data.status_code){
                case 'SYSTEM_1000':
                    dispatch(getIdolArray());
                    modal1000.current.show();
                    setRedirectTimeout(() => navigate(`/idol/${params.idolUuid}`), 2000);
                    break;
                case 'USER_2021':
                    modal2021.current.show();
                    break
                default:
                    setCurrentStatusCode(res.data.status_code);
                    statusModalRef.current?.show();
                    break
            }
        } catch (error) {
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Update idol error:', error);
            }
        } finally {
            setIsLoading(false);
        }
    }

    // 刪除偶像
    const handleDeleteClick = () => {
        modalDelete.current.show();
    };

    const deleteIdol = async () => {
        modalDelete.current.hide();
        setIsLoading(true);
        try {
            const statusCode = await deleteIdolAndSwitch(info.id);
            if (statusCode === 'SYSTEM_1000') {
                modalDeleteSuccess.current.show();
                setRedirectTimeout(() => {
                    navigate('/');
                }, 1500);
            } else if (statusCode !== 'NETWORK_ERROR') {
                // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
                setCurrentStatusCode(statusCode);
                statusModalRef.current?.show();
            }
        } catch (error) {
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Delete idol error:', error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(()=> {
        getIdolInfo();
    }, []);

    // Cleanup preview URLs on component unmount
    useEffect(() => {
        return () => {
            if (avatarPreview && avatarPreview.startsWith('blob:')) {
                URL.revokeObjectURL(avatarPreview);
            }
            if (coverPreview && coverPreview.startsWith('blob:')) {
                URL.revokeObjectURL(coverPreview);
            }
        };
    }, [avatarPreview, coverPreview]);

    return (
        <>
            <MainNav />
            <Container fluid="lg" className="nav-padding">
                <h2 className="mt-4 mb-4">{t('editIdolProfile')}</h2>
                <div className="text-end mb-3 d-flex gap-2 justify-content-end">
                    {editingIdol && canDeleteIdol(editingIdol, idolList) && (
                        <Button className="btn btn-danger rounded-pill" onClick={handleDeleteClick}>{t('delete_idol_button')}</Button>
                    )}
                    <Button href="/" className="btn btn-neutral rounded-pill">{t('skip')}</Button>
                </div>
                <div className="border rounded-3 p-3">
                {/* 抓不到資料就不渲染表單：initInfo 會停在 {}，差異比對會把每個欄位都當成有異動，
                    使用者在一張看起來正常的空表單上按儲存，等於把整份偶像資料清成空字串 */}
                {isInfoLoaded ? (
                   <Col lg={12} md={12} sm={12}>
                    <Form noValidate validated={validated} className="idol-create-section" onSubmit={ handleSubmit }>
                        <Row>
                            <Col lg={6} md={12} sm={12}>
                                <div className="idol-pic mb-4">
                                    <div className="p-2 w-100 border-bottom">
                                        <h5 className="mb-0">{t('edit_avater')}</h5>
                                        <small className="text-gray">{t('avater_remark')}</small>
                                    </div>
                                    <div className="pic stickers py-3">
                                        <div className="position-relative d-inline-block" style={{cursor: 'pointer'}} onClick={() => document.getElementById('avatar-input').click()}>
                                            <img src={avatarPreview || info.idol_big_avatar_url || placeholder_portrait_300x300}></img>
                                            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded-circle" style={{backgroundColor: 'rgba(0,0,0,0.4)'}}>
                                                <FontAwesomeIcon icon={["fas", "camera"]} size="2x" className="text-white" />
                                            </div>
                                        </div>
                                        <Form.Control
                                            id="avatar-input"
                                            type="file"
                                            accept="image/png, image/jpg, image/jpeg"
                                            onChange={ handleChange }
                                            name="avatar_path"
                                            style={{display: 'none'}}
                                        />


                                    </div>
                                </div>
                            </Col>

                            <Col lg={6} md={12} sm={12}>
                                <div className="idol-pic">
                                    <div className="p-2 w-100 border-bottom">
                                        <h5 className="mb-0">{t('edit_cover')}</h5>
                                        <small className="text-gray">{t('cover_remark')}</small>
                                    </div>
                                    <div className="pic cover py-3 w-100">
                                        <div className="position-relative w-100" style={{cursor: 'pointer'}} onClick={() => document.getElementById('cover-input').click()}>
                                            <img src={coverPreview || info.idol_big_cover_url || placeholder_cover_1600x700} className="w-100"></img>
                                            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded" style={{backgroundColor: 'rgba(0,0,0,0.4)'}}>
                                                <FontAwesomeIcon icon={["fas", "camera"]} size="2x" className="text-white" />
                                            </div>
                                        </div>
                                        <Form.Control
                                            id="cover-input"
                                            type="file"
                                            accept="image/png, image/jpg, image/jpeg"
                                            onChange={ handleChange }
                                            name="cover_path"
                                            style={{display: 'none'}}
                                        />
                                    </div>
                                </div>
                            </Col>

                            <Col lg={12} md={12} sm={12}>
                                <div className="pb-2 d-flex align-items-center justify-content-between w-100 border-bottom mb-3">
                                    <h5 className="mb-0">{t('information')}</h5>
                                </div>
                            </Col>

                            <Col xl={3} md={6} sm={12}>
                                <Form.Group className="mb-3" controlId="idol_nick_name">
                                    <Form.Label>{t('nickname')} <span className="require-star">*</span></Form.Label>
                                    <Form.Control
                                        onChange={ handleChange }
                                        name="idol_nick_name"
                                        type="text"
                                        value={ info.idol_nick_name }
                                        autoComplete="username"
                                        disabled
                                    />
                                </Form.Group>
                            </Col>

                            <Col xl={3} md={6} sm={12}>
                                <Form.Group className="mb-3" controlId="idol_full_name">
                                    <Form.Label>{t('fullname')}</Form.Label>
                                    <Form.Control
                                        onChange={ handleChange }
                                        name="idol_full_name"
                                        value={info.idol_full_name ?? ''}
                                        type="text"
                                        autoComplete="name"
                                        maxLength={100}
                                        pattern=".{0,100}"
                                    />
                                    <Form.Control.Feedback type="invalid">{t('in_words', { count: 100 })}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col xl={3} md={6} sm={12}>
                                <Form.Group className="mb-3" controlId="age">
                                    <Form.Label>{t('age')}</Form.Label>
                                    <Form.Control
                                        onChange={ handleChange }
                                        name="age"
                                        value={info.age ?? ''}
                                        type="number"
                                        autoComplete="off"
                                        min="1"
                                        max="100"
                                        />
                                        <Form.Control.Feedback type="invalid">{t('between 0-255')}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col xl={3} md={6} sm={12}>
                                <Form.Group className="mb-3" controlId="gender">
                                    <Form.Label>{t('gender')}</Form.Label>
                                    <Form.Select
                                     onChange={ handleChange }
                                     name="gender"
                                     value={info.gender ?? ''}
                                     autoComplete="sex"
                                     >
                                        <option value="">{t('select_gender')}</option>
                                        <option value="0">{t('male')}</option>
                                        <option value="1">{t('female')}</option>
                                        <option value="2">{t('other')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col xl={3} md={6} sm={12}>
                                <Form.Group className="mb-3" controlId="height">
                                    <Form.Label>{t('height')}</Form.Label>
                                    <Form.Control
                                        onChange={ handleChange }
                                        name="height"
                                        value={info.height ?? ''}
                                        type="number"
                                        autoComplete="off"
                                        min="0"
                                        max="255"
                                    />
                                    <Form.Control.Feedback type="invalid">{t('between 0-255')}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col xl={3} md={6} sm={12}>
                                <Form.Group className="mb-3" controlId="body_type">
                                    <Form.Label>{t('weight')}</Form.Label>
                                    <Form.Control
                                        onChange={ handleChange }
                                        name="body_type"
                                        value={info.body_type ?? ''}
                                        type="text"
                                        autoComplete="off"
                                        pattern=".{0,100}"
                                    />
                                    <Form.Control.Feedback type="invalid">{t('in_words', { count: 100 })}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col xl={3} md={6} sm={12}>
                                <Form.Group className="mb-3" controlId="sexual_orientation">
                                    <Form.Label>{t('sexual_orientation')}</Form.Label>
                                    <Form.Select
                                        onChange={ handleChange }
                                        name="sexual_orientation"
                                        value={info.sexual_orientation ?? ''}
                                        autoComplete="off"
                                    >
                                        <option value="">{t('select_sexual_orientation')}</option>
                                        <option value="Heterosexual">{t('heterosexual')}</option>
                                        <option value="Homosexual">{t('homosexual')}</option>
                                        <option value="Bisexual">{t('bisexual')}</option>
                                        <option value="Pansexual">{t('pansexual')}</option>
                                        <option value="Asexual">{t('asexual')}</option>
                                        <option value="Other">{t('other')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col xl={3} md={6} sm={12}>
                                <Form.Group className="mb-3" controlId="relationship_status">
                                    <Form.Label>{t('relationship_status')}</Form.Label>
                                    <Form.Select
                                        onChange={ handleChange }
                                        name="relationship_status"
                                        value={info.relationship_status ?? ''}
                                        autoComplete="off"
                                    >
                                        <option value="">{t('select_relationship_status')}</option>
                                        <option value="Single">{t('single')}</option>
                                        <option value="In_relationship">{t('in_relationship')}</option>
                                        <option value="Engaged">{t('engaged')}</option>
                                        <option value="Married">{t('married')}</option>
                                        <option value="Open_relationship">{t('open_relationship')}</option>
                                        <option value="Complicated">{t('complicated')}</option>
                                        <option value="Divorced">{t('divorced')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col xl={6} md={6} sm={12}>
                                <Form.Group className="mb-3" controlId="education">
                                    <Form.Label>{t('education')}</Form.Label>
                                    <Form.Control
                                        onChange={ handleChange }
                                        name="education"
                                        value={info.education ?? ''}
                                        type="text"
                                        autoComplete="off"
                                        pattern=".{0,100}"
                                        />
                                        <Form.Control.Feedback type="invalid">{t('in_words', { count: 100 })}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col xl={6} md={6} sm={12}>
                                <Form.Group className="mb-3" controlId="occupation">
                                    <Form.Label>{t('occupation')}</Form.Label>
                                    <Form.Control
                                        onChange={ handleChange }
                                        name="occupation"
                                        value={info.occupation ?? ''}
                                        type="text"
                                        autoComplete="organization-title"
                                        pattern=".{0,100}"
                                        />
                                        <Form.Control.Feedback type="invalid">{t('in_words', { count: 100 })}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col xl={6} md={6} sm={12}>
                                <Form.Group className="mb-3" controlId="interests">
                                    <Form.Label>{t('interest')}</Form.Label>
                                    <Form.Control
                                        onChange={ handleChange }
                                        name="interests"
                                        value={info.interests ?? ''}
                                        type="text"
                                        autoComplete="off"
                                        pattern=".{0,100}"
                                    />
                                        <Form.Control.Feedback type="invalid">{t('in_words', { count: 100 })}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col xl={6} md={6} sm={12}>
                                <Form.Group className="mb-3" controlId="preferences">
                                    <Form.Label>{t('content_type')}</Form.Label>
                                    <Form.Select
                                        onChange={ handleChange }
                                        name="preferences"
                                        value={info.preferences ?? ''}
                                        autoComplete="off"
                                    >
                                        <option value="">{t('select_content_type')}</option>
                                        <option value="beauty_skincare">{t('beauty_skincare')}</option>
                                        <option value="fashion_style">{t('fashion_style')}</option>
                                        <option value="food_travel">{t('food_travel')}</option>
                                        <option value="lifestyle">{t('lifestyle')}</option>
                                        <option value="relationships">{t('relationships')}</option>
                                        <option value="tech_3c">{t('tech_3c')}</option>
                                        <option value="knowledge_skills">{t('knowledge_skills')}</option>
                                        <option value="art_design">{t('art_design')}</option>
                                        <option value="photography">{t('photography')}</option>
                                        <option value="sports_fitness">{t('sports_fitness')}</option>
                                        <option value="adult_content">{t('adult_content')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col xl={12} md={12} sm={12}>
                                <Form.Group className="mb-3" controlId="idol_bio">
                                    <Form.Label>{t('bio')}</Form.Label>
                                    <Form.Control
                                        onChange={ handleChange }
                                        name="idol_bio"
                                        value={info.idol_bio ?? ''}
                                        as="textarea"
                                        rows={6}
                                        autoComplete="off"
                                    />
                                </Form.Group>
                            </Col>

                            <Col xl={12} md={12} sm={12}>
                                <Form.Group className="mb-3" controlId="prompt_word">
                                    <Form.Label>{t('prompt_word')}</Form.Label>
                                    <Form.Control
                                        onChange={ handleChange }
                                        name="prompt_word"
                                        value={info.prompt_word ?? ''}
                                        as="textarea"
                                        rows={6}
                                        autoComplete="off"
                                        />
                                </Form.Group>
                            </Col>

                            <Col xl={6} md={6} sm={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('is_it_original')}</Form.Label>
                                    <div className="mb-3">
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('yes')}
                                            name="is_original"
                                            type="radio"
                                            id="is_original_yes"
                                            value="1"
                                            checked={info.is_original === 1}
                                        />
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('no')}
                                            name="is_original"
                                            type="radio"
                                            id="is_original_no"
                                            value="0"
                                            checked={info.is_original === 0}
                                        />
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col xl={6} md={6} sm={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('is_nsfw')}</Form.Label>
                                    <div className="mb-3">
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('yes')}
                                            name="is_nsfw"
                                            type="radio"
                                            id="is_nsfw_yes"
                                            value="1"
                                            checked={info.is_nsfw === 1}
                                        />
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('no')}
                                            name="is_nsfw"
                                            type="radio"
                                            id="is_nsfw_no"
                                            value="0"
                                            checked={info.is_nsfw === 0}
                                        />
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col lg={12} md={12} sm={12}>
                                <div className="pb-2 d-flex align-items-center justify-content-between w-100 border-bottom mb-3 mt-3">
                                    <h5 className="mb-0">{t('privacy_setting')}</h5>
                                    {/* 0=不公開, 1=公開 */}
                                </div>
                            </Col>

                            <Col xl={4} md={4} sm={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('show_age')}</Form.Label>
                                    <div className="mb-3">
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('yes')}
                                            name="private_age"
                                            type="radio"
                                            id="private_age_yes"
                                            value="1"
                                            defaultChecked={true}
                                        />
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('no')}
                                            name="private_age"
                                            type="radio"
                                            id="private_age_no"
                                            value="0"
                                        />
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col xl={4} md={4} sm={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('show_height')}</Form.Label>
                                    <div className="mb-3">
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('yes')}
                                            name="private_height"
                                            type="radio"
                                            id="private_height_yes"
                                            value="1"
                                            defaultChecked={true}
                                        />
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('no')}
                                            name="private_height"
                                            type="radio"
                                            id="private_height_no"
                                            value="0"
                                        />
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col xl={4} md={4} sm={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('show_weight')}</Form.Label>
                                    <div className="mb-3">
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('yes')}
                                            name="private_body_type"
                                            type="radio"
                                            id="private_body_type_yes"
                                            value="1"
                                            defaultChecked={true}
                                        />
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('no')}
                                            name="private_body_type"
                                            type="radio"
                                            id="private_body_type_no"
                                            value="0"
                                        />
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col xl={4} md={4} sm={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('show_gender')}</Form.Label>
                                    <div className="mb-3">
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('yes')}
                                            name="private_gender"
                                            type="radio"
                                            id="private_gender_yes"
                                            value="1"
                                            defaultChecked={true}
                                        />
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('no')}
                                            name="private_gender"
                                            type="radio"
                                            id="private_gender_no"
                                            value="0"
                                        />
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col xl={4} md={4} sm={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('show_sexual_orientation')}</Form.Label>
                                    <div className="mb-3">
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('yes')}
                                            name="private_sexual_orientation"
                                            type="radio"
                                            id="private_sexual_orientation_yes"
                                            value="1"
                                            defaultChecked={true}
                                        />
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('no')}
                                            name="private_sexual_orientation"
                                            type="radio"
                                            id="private_sexual_orientation_no"
                                            value="0"
                                        />
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col xl={4} md={4} sm={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('show_relationship_status')}</Form.Label>
                                    <div className="mb-3">
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('yes')}
                                            name="private_relationship_status"
                                            type="radio"
                                            id="private_relationship_status_yes"
                                            value={1}
                                            defaultChecked={true}
                                        />
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('no')}
                                            name="private_relationship_status"
                                            type="radio"
                                            id="private_relationship_status_no"
                                            value={0}
                                        />
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col xl={4} md={4} sm={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('show_education')}</Form.Label>
                                    <div className="mb-3">
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('yes')}
                                            name="private_education"
                                            type="radio"
                                            id="private_education_yes"
                                            value="1"
                                            defaultChecked={true}
                                        />
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('no')}
                                            name="private_education"
                                            type="radio"
                                            id="private_education_no"
                                            value="0"
                                        />
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col xl={4} md={4} sm={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('show_occupation')}</Form.Label>
                                    <div className="mb-3">
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('yes')}
                                            name="private_occupation"
                                            type="radio"
                                            id="private_occupation_yes"
                                            value="1"
                                            defaultChecked={true}
                                        />
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('no')}
                                            name="private_occupation"
                                            type="radio"
                                            id="private_occupation_no"
                                            value="0"
                                        />
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col xl={4} md={4} sm={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('show_interest')}</Form.Label>
                                    <div className="mb-3">
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('yes')}
                                            name="private_interests"
                                            type="radio"
                                            id="private_interests_yes"
                                            value="1"
                                            defaultChecked={true}
                                        />
                                        <Form.Check
                                            onChange={ handleChange }
                                            inline
                                            label={t('no')}
                                            name="private_interests"
                                            type="radio"
                                            id="private_interests_no"
                                            value="0"
                                        />
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col lg={12} md={12} sm={12} className="text-center mt-4">
                                <Button href="/"  className="btn btn-neutral rounded-pill me-3" size="lg">{t('cancel')}</Button>
                                <LoadingButton className="btn btn-primary rounded-pill btn-lg" type="submit"
                                disabled={!isFormChanged || !isInfoLoaded} isLoading={isLoading}>{t('save')}</LoadingButton>
                            </Col>
                        </Row>

                    </Form>
                </Col>
                ) : (
                   <div className="text-center py-5">
                       {isInfoLoading ? (
                           <span className="spinner-border text-primary" role="status">
                               <span className="visually-hidden">{t('loading')}</span>
                           </span>
                       ) : (
                           <>
                               <p className="mb-3">{t('load_failed', { type: t('profile') })}</p>
                               <Button className="btn btn-primaryF rounded-pill" onClick={getIdolInfo}>{t('retry_load')}</Button>
                           </>
                       )}
                   </div>
                )}
                </div>
            </Container>
            <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />

            <MessageModal ref={modal1000} status="success">
                <p>{t('update_success')}</p>
            </MessageModal>
            <MessageModal ref={modal2021} status="error">
                <p>{t('update_error1')}</p>
            </MessageModal>
            <MessageModal ref={modalSize} status="error">
                <p>{t('update_error2')}</p>
            </MessageModal>
            <MessageModal ref={modalType} status="error">
                <p>{t('update_error3')}</p>
            </MessageModal>
            <MessageModal ref={modalImageProcessError} status="error">
                <p>{t('image_process_failed')}</p>
            </MessageModal>
            <MessageModal ref={modalDelete} status="warning" size="md">
                <h5 className="mb-5">{t('delete_idol')}</h5>
                <div className="text-end">
                    <button type="button" className="btn btn-neutral rounded-pill me-3" onClick={() => modalDelete.current.hide()}>{t('cancel')}</button>
                    <LoadingButton className="btn btn-primary rounded-pill" onClick={deleteIdol} isLoading={isLoading}>{t('confirm')}</LoadingButton>
                </div>
            </MessageModal>
            <MessageModal ref={modalDeleteSuccess} status="success">
                <p>{t('delete_success')}</p>
            </MessageModal>
        </>
    )
}
export default IdolUpdate